import { Game, GameEngine, chooseBotAction, invariant } from '../../../packages/game-core/src/index.js';
import { OfflineSaveStore, SAVE_VERSION } from './saveStore.js';

const METHODS = {
  'game:roll': ['rollDice'], 'game:end-turn': ['endTurn'],
  'property:buy': ['buyProperty'], 'property:decline': ['declineProperty'],
  'property:build': ['build', 'propertyId'], 'property:sell-building': ['sellBuilding', 'propertyId'],
  'property:mortgage': ['mortgage', 'propertyId'], 'property:unmortgage': ['unmortgage', 'propertyId'],
  'jail:pay': ['payJailFine'], 'jail:use-card': ['useJailCard'],
  'debt:pay': ['resolveDebt'], 'bankruptcy:declare': ['declareBankruptcy'],
  'trade:create': ['createTrade', 'proposal'], 'trade:accept': ['acceptTrade', 'tradeId'], 'trade:decline': ['declineTrade', 'tradeId'],
};
const actionId = () => [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, '0')).join('');

export class OfflineGameController {
  constructor({ game, mode, viewerId, handoff = null, store = new OfflineSaveStore(), random = Math.random, botDelay = 850, persistedSnapshot = null }) {
    this.game = game;
    this.engine = new GameEngine(game, { random });
    this.mode = mode;
    this.viewerId = viewerId;
    this.handoff = handoff;
    this.store = store;
    this.botDelay = botDelay;
    this.listeners = new Set();
    this.queue = Promise.resolve();
    this.running = false;
    this.timer = null;
    this.persistedSnapshot = persistedSnapshot ? structuredClone(persistedSnapshot) : null;
    this.storageFailure = null;
    this.resumeAfterSave = false;
    this.recovery = null;
    this.publish();
  }

  static async create({ mode, players, botCount = 0 }, options = {}) {
    invariant(['bots', 'hotseat'].includes(mode), 'INVALID_MODE', 'Выберите режим игры.');
    invariant(mode === 'bots' ? players.length === 1 && Number.isInteger(botCount) && botCount >= 1 && botCount <= 3 : players.length >= 2 && players.length <= 4 && botCount === 0, 'INVALID_PLAYERS', 'Нужно от 2 до 4 участников.');
    const game = new Game();
    const controller = new OfflineGameController({ game, mode, ...options });
    const expected = Object.hasOwn(options, 'expectedSave') ? options.expectedSave : await controller.store.read();
    for (const player of players) {
      const name = String(player.name ?? '').trim();
      invariant(name.length >= 1 && name.length <= 20, 'INVALID_NAME', 'Имя: от 1 до 20 символов.');
      controller.engine.addPlayer({ name, token: player.token });
    }
    if (botCount) controller.engine.setBotCount(botCount);
    controller.engine.start();
    controller.viewerId = mode === 'bots' ? game.players.find((player) => !player.isBot).id : game.currentPlayer.id;
    if (mode === 'hotseat') controller.handoff = { playerId: controller.viewerId, purpose: 'turn' };
    const snapshot = controller.snapshot();
    await controller.store.write(snapshot, { expected });
    controller.persistedSnapshot = structuredClone(snapshot);
    controller.publish();
    return controller;
  }

  static async restore(store = new OfflineSaveStore(), options = {}) {
    const save = await store.read();
    invariant(save?.game?.status === 'active', 'NO_LOCAL_GAME', 'Нет незавершённой локальной игры.');
    return new OfflineGameController({ ...save, game: Game.fromSnapshot(save.game), store, ...options, persistedSnapshot: save, handoff: save.mode === 'hotseat' ? (save.handoff ?? { playerId: save.viewerId, purpose: 'turn' }) : null });
  }

  snapshot() {
    return { schemaVersion: SAVE_VERSION, mode: this.mode, viewerId: this.viewerId, handoff: this.handoff, updatedAt: Date.now(), game: this.game.toSnapshot() };
  }

  publish(error = null) {
    this.state = { code: 'ОФЛАЙН', offline: true, mode: this.mode, viewerId: this.viewerId, hostId: this.game.players[0]?.id, isSpectator: false, handoff: this.handoff, game: this.game.getPublicState(this.viewerId), error: error ?? this.storageFailure?.message ?? null, storageBlocked: Boolean(this.storageFailure), saveConflict: this.storageFailure?.code === 'SAVE_CONFLICT' };
    for (const listener of this.listeners) listener(this.state);
  }

  subscribe(listener) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  start() { if (this.storageFailure) { this.resumeAfterSave = true; return; } this.running = true; this.schedule(); }
  close() { this.running = false; this.resumeAfterSave = false; clearTimeout(this.timer); this.timer = null; }
  async pause() { this.close(); await this.queue; }

  transact(mutate) {
    const result = this.queue.then(async () => {
      invariant(!this.storageFailure, 'SAVE_REQUIRED', 'Игра приостановлена. Повторите сохранение или загрузите актуальную партию.');
      const before = this.snapshot();
      let saving = false;
      let response;
      let saved;
      try {
        response = mutate();
        const snapshot = this.snapshot();
        saved = structuredClone(snapshot);
        saving = true;
        await this.store.write(snapshot, { expected: this.persistedSnapshot });
        saving = false;
      } catch (error) {
        this.game = Game.fromSnapshot(before.game);
        this.engine.game = this.game;
        this.viewerId = before.viewerId;
        this.handoff = before.handoff;
        if (saving) {
          const resume = this.running;
          this.close();
          this.resumeAfterSave = resume;
          this.storageFailure = error;
        }
        this.publish(error.message);
        throw error;
      }
      this.persistedSnapshot = saved;
      this.publish();
      this.schedule();
      return response;
    });
    this.queue = result.catch(() => undefined);
    return result;
  }

  retrySave() {
    if (this.recovery) return this.recovery;
    const recovery = this.queue.then(async () => {
      if (!this.storageFailure) return;
      invariant(this.storageFailure.code !== 'SAVE_CONFLICT', 'SAVE_CONFLICT', 'Загрузите актуальную партию: сохранение изменилось в другой вкладке.');
      let saved;
      try {
        const snapshot = this.snapshot();
        saved = structuredClone(snapshot);
        await this.store.write(snapshot, { expected: this.persistedSnapshot });
      } catch (error) {
        this.storageFailure = error;
        this.publish();
        throw error;
      }
      this.persistedSnapshot = saved;
      this.storageFailure = null;
      const resume = this.resumeAfterSave;
      this.resumeAfterSave = false;
      this.running = resume;
      this.publish();
      this.schedule();
    });
    this.recovery = recovery.finally(() => { this.recovery = null; });
    this.queue = this.recovery.catch(() => undefined);
    return this.recovery;
  }

  reloadSave() {
    if (this.recovery) return this.recovery;
    const recovery = this.queue.then(async () => {
      const save = await this.store.read();
      invariant(['active', 'finished'].includes(save?.game?.status), 'NO_LOCAL_GAME', 'Нет сохранённой локальной игры.');
      const resume = this.running || this.resumeAfterSave;
      this.close();
      this.game = Game.fromSnapshot(save.game);
      this.engine.game = this.game;
      this.mode = save.mode;
      this.viewerId = save.viewerId;
      this.handoff = this.mode === 'hotseat' ? (save.handoff ?? { playerId: save.viewerId, purpose: 'turn' }) : null;
      this.persistedSnapshot = structuredClone(save);
      this.storageFailure = null;
      this.running = resume;
      this.publish();
      this.schedule();
    });
    this.recovery = recovery.catch(error => { this.publish(error.message); throw error; }).finally(() => { this.recovery = null; });
    this.queue = this.recovery.catch(() => undefined);
    return this.recovery;
  }

  updateHandoff(purpose = 'turn', targetId = null) {
    if (this.mode !== 'hotseat' || this.game.status !== 'active') return;
    const debtorId = this.game.pendingDebt?.debtorId;
    const nextId = debtorId ?? targetId ?? this.game.currentPlayer.id;
    if (nextId !== this.viewerId) {
      this.viewerId = nextId;
      this.handoff = { playerId: nextId, purpose: debtorId ? 'debt' : purpose };
    }
  }

  confirmHandoff() { return this.transact(() => { this.handoff = null; }); }

  action(event, payload = {}) {
    const actorId = this.viewerId;
    const revision = payload.expectedRevision ?? this.game.revision;
    const id = payload.actionId ?? actionId();
    return this.transact(() => {
      invariant(!this.handoff, 'HANDOFF_REQUIRED', 'Сначала передайте устройство и начните ход.');
      const mapping = METHODS[event];
      invariant(mapping, 'INVALID_ACTION', 'Неизвестное действие.');
      if (this.game.findProcessedAction(actorId, id)) return { ok: true, duplicate: true };
      invariant(this.game.revision === revision, 'STALE_ACTION', 'Состояние изменилось. Повторите действие.');
      const arg = mapping[1] === 'proposal' ? payload : mapping[1] === 'propertyId' ? Number(payload.propertyId) : payload[mapping[1]];
      const result = this.engine[mapping[0]](actorId, ...(mapping[1] ? [arg] : []));
      this.game.recordProcessedAction(actorId, id, event);
      this.game.revision += 1;
      this.updateHandoff(event === 'trade:create' ? 'trade' : 'turn', event === 'trade:create' ? result.toPlayerId : null);
      return { ok: true, duplicate: false };
    });
  }

  async stepBot() {
    return this.transact(() => {
      const action = chooseBotAction(this.game);
      if (!action) return null;
      this.engine[action.method](action.player.id, ...action.args);
      if (action.actionId) this.game.recordProcessedAction(action.player.id, action.actionId, 'bot:trade:create');
      this.game.revision += 1;
      this.updateHandoff();
      return action.method;
    });
  }

  schedule(delay = this.botDelay) {
    if (!this.running || this.storageFailure || this.timer || this.handoff || !chooseBotAction(this.game)) return;
    const timer = setTimeout(async () => {
      // Keep the timer set while the queued operation runs, preventing a second bot timer.
      try {
        const method = await this.stepBot();
        if (this.timer !== timer) return;
        this.timer = null;
        this.schedule(method === 'rollDice' ? Math.max(this.botDelay, 1200 + (this.game.lastMove?.path.length ?? 0) * 190) : this.botDelay);
      } catch {
        if (this.timer !== timer) return;
        this.timer = null;
        if (!this.storageFailure) this.close();
      }
    }, delay);
    this.timer = timer;
  }
}
